<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { ChevronDown, X } from 'lucide-vue-next'

type Item = {
  id: string
  label: string
  disabled?: boolean
}

const props = withDefaults(
  defineProps<{
    modelValue: string[] | undefined
    items: Item[]
    placeholder?: string
    disabled?: boolean
    searchable?: boolean
    maxSelected?: number
  }>(),
  {
    placeholder: 'Select items...',
    disabled: false,
    searchable: false,
    maxSelected: undefined,
    modelValue: () => [] 
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
  (e: 'change', value: string[]): void
}>()

const isOpen = ref(false)
const searchQuery = ref('')
const inputRef = ref<HTMLInputElement | null>(null)
const dropdownRef = ref<HTMLDivElement | null>(null)

const selectedItems = computed(() => {
  return props.items.filter(item => props.modelValue.includes(item.id))
})

const filteredItems = computed(() => {
  if (!props.searchable || !searchQuery.value) return props.items
  return props.items.filter(item =>
    item.label.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const availableItems = computed(() => {
  return filteredItems.value.filter(
    item => !props.modelValue.includes(item.id)
  )
})

const isMaxSelected = computed(() => {
  return props.maxSelected !== undefined && 
         props.modelValue.length >= props.maxSelected
})

function toggleDropdown() {
  if (props.disabled) return
  
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    nextTick(() => {
      inputRef.value?.focus()
    })
  }
}

function selectItem(item: Item) {
  if (item.disabled || isMaxSelected.value) return
  
  const newValue = [...props.modelValue, item.id]
  emit('update:modelValue', newValue)
  emit('change', newValue)
  searchQuery.value = ''
  
  if (props.maxSelected && newValue.length >= props.maxSelected) {
    isOpen.value = false
  }
}

function removeItem(value: string) {
  const newValue = props.modelValue.filter(v => v !== value)
  emit('update:modelValue', newValue)
  emit('change', newValue)
}

function clearAll() {
  emit('update:modelValue', [])
  emit('change', [])
  isOpen.value = false
}

function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

watch(isOpen, (open) => {
  if (open) {
    document.addEventListener('click', handleClickOutside)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})
</script>

<template>
  <div class="relative w-full" ref="dropdownRef">
    <div
      class="flex flex-wrap items-center gap-2 min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
      :class="{
        'cursor-not-allowed opacity-50': disabled,
        'cursor-pointer': !disabled
      }"
      @click="toggleDropdown"
    >
      <div v-if="selectedItems.length === 0" class="text-muted-foreground">
        {{ placeholder }}
      </div>

      <div v-else class="flex flex-wrap gap-2">
        <div
          v-for="item in selectedItems"
          :key="item.id"
          class="flex items-center gap-1 rounded-full bg-accent px-2 py-1 text-xs"
        >
          <span>{{ item.label }}</span>
          <button
            type="button"
            class="rounded-full p-0.5 hover:bg-accent-foreground hover:text-accent"
            @click.stop="removeItem(item.id)"
            :disabled="disabled"
          >
            <X class="h-3 w-3" />
          </button>
        </div>
      </div>

      <input
        v-if="searchable && isOpen"
        ref="inputRef"
        v-model="searchQuery"
        type="text"
        class="flex-1 min-w-[50px] bg-transparent outline-none"
        @click.stop
      />

      <div class="ml-auto flex items-center gap-1">
        <button
          v-if="selectedItems.length > 0"
          type="button"
          class="rounded-full p-0.5 text-muted-foreground hover:bg-accent"
          @click.stop="clearAll"
          :disabled="disabled"
        >
          <X class="h-4 w-4" />
        </button>
        <div class="h-5 w-px bg-border"></div>
        <ChevronDown
          class="h-4 w-4 text-muted-foreground transition-transform duration-200"
          :class="{ 'rotate-180': isOpen }"
        />
      </div>
    </div>

    <Transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-if="isOpen"
        class="absolute z-50 mt-1 w-full min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
      >
        <ul class="max-h-60 overflow-auto py-1 text-sm">
          <li
            v-if="availableItems.length === 0"
            class="px-2 py-1.5 text-center text-sm text-muted-foreground"
          >
            No items found
          </li>

          <li
            v-for="item in availableItems"
            :key="item.id"
            class="relative flex cursor-default select-none items-center px-2 py-1.5 outline-none hover:bg-accent hover:text-accent-foreground"
            :class="{
              'cursor-not-allowed opacity-50': item.disabled,
              'cursor-pointer': !item.disabled
            }"
            @click.stop="selectItem(item)"
          >
            <span>{{ item.label }}</span>
          </li>

          <li
            v-if="isMaxSelected"
            class="px-2 py-1.5 text-center text-xs text-muted-foreground"
          >
            Maximum {{ maxSelected }} items selected
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>
